import { PostfixOperandSchema, PostfixOptions, PreviousStatePredicate } from 'components/PostfixNew/types';
import React, { createContext, useContext, useMemo, useRef } from 'react';
import isEqual from 'react-fast-compare';

import { OperandAutocompleteOptions, View } from './index.view';
import { PostfixAutocompleteProperty } from './types';

export const AutocompleteContext = createContext<{
  value: PostfixOperandSchema<{
    properties: PostfixAutocompleteProperty[];
  }>['properties'];
  onChange: PreviousStatePredicate<PostfixOperandSchema<PostfixOptions>>;
} | null>(null);

export function OperandAutocomplete<T extends PostfixOptions>(props: {
  style?: 'input' | 'select';
  property: PostfixAutocompleteProperty;
  value: PostfixOperandSchema<{
    properties: PostfixAutocompleteProperty[];
  }>;
  onChange: PreviousStatePredicate<PostfixOperandSchema<T>>;
  compact?: boolean;
}) {
  const { property, value, onChange, compact } = props;

  const previousValue = useRef<unknown>();
  const previousOptions = useRef<OperandAutocompleteOptions | null>(null);

  const options = useMemo<OperandAutocompleteOptions>(() => {
    const newOptions: OperandAutocompleteOptions = {
      selectOptions: property.options.selectOptions(value),
      hidden: property.options.hidden?.(value) ?? false,
      allowMultiple: property.options.allowMultiple?.(value) ?? false,
      required: property.options.required?.(value) ?? false,
      allowCustomValues: property.options.allowCustomValues?.(value),
      customInputType: property.options.customInputType?.(value) ?? 'string',
      startDropdownAdornment: property.options.startDropdownAdornment?.(value),
      label: property.options.label?.(value) ?? '',
      tabs: property.options.tabs?.(value),
      labelPrefix: property.options.labelPrefix?.(value) ?? '',
      disabled: property.options.disabled?.(value) ?? false,
    };

    if (!isEqual(previousOptions.current, newOptions)) {
      previousOptions.current = newOptions;
    }

    return previousOptions.current ?? newOptions;
  }, [property.options, value]);

  const currentValue = useMemo(() => {
    const newValue = value.properties[property.name];

    if (!isEqual(previousValue.current, newValue)) {
      previousValue.current = newValue;
    }

    return previousValue.current;
  }, [property.name, value.properties]);

  const view = useMemo(() => {
    return (
      <View
        property={property}
        onChange={onChange}
        compact={compact}
        currentValue={currentValue}
        options={options}
        style={props.style}
      />
    );
  }, [compact, currentValue, onChange, options, property, props.style]);

  if (options.hidden) {
    return null;
  }

  return (
    <AutocompleteContext.Provider value={{ value: value.properties, onChange }}>{view}</AutocompleteContext.Provider>
  );
}

export function useAutocompleteContext() {
  const selectContext = useContext(AutocompleteContext);

  return selectContext;
}
